import { create } from "zustand";
import { getPermissions } from "@/api/rbac";
import { getSubscriptionPermissions } from "@/features/settings/api/subscriptionService";
import type {
  AddonPermission,
  SubscriptionPermission,
} from "@/features/settings/api/subscriptionService";

export type { AddonPermission };

interface PermissionsState {
  /** RBAC permission codes granted to the current user. */
  permissions: string[];
  /** Plan feature codes (e.g. "EMISSIONS", "SRM") the subscription includes. */
  subscriptionPermissions: string[];
  planPermissions: SubscriptionPermission[];
  addonPermissions: AddonPermission[];
  /** Subscription status (e.g. "trial", "active", "unsubscribed"). */
  subscriptionStatus?: string;
  planCode?: string;
  cardStatus?: string;
  currentTermEndsAt?: string;
  trialEndsAt?: string;
  loaded: boolean;
  loading: boolean;
  permissionsLoaded: boolean;
  subscriptionLoaded: boolean;
  load: () => Promise<void>;
  reset: () => void;
}

/**
 * Extract plan feature names from the subscription permission objects and
 * inject synthetic SRM / SUPPLIER permissions when the SRM addon is present
 * (mirrors the legacy `setSubscriptionPermissions` logic).
 */
function buildSubscriptionPermissions(
  raw: Array<{ name: string; usageLimit: number | null }> | undefined,
  addons: AddonPermission[] | undefined,
): string[] {
  const names = (raw ?? []).map((p) => p.name);

  for (const addon of addons ?? []) {
    if (Number(addon.quantity) > 0 && addon.name && !names.includes(addon.name))
      names.push(addon.name);
    if (
      Number(addon.quantity) > 0 &&
      addon.name === "SUPPLIER" &&
      !names.includes("SRM")
    )
      names.push("SRM");
  }

  return names;
}

// A reset/new request must invalidate any older in-flight permission response.
let loadVersion = 0;

export const usePermissionsStore = create<PermissionsState>((set) => ({
  permissions: [],
  subscriptionPermissions: [],
  planPermissions: [],
  addonPermissions: [],
  subscriptionStatus: undefined,
  planCode: undefined,
  cardStatus: undefined,
  currentTermEndsAt: undefined,
  trialEndsAt: undefined,
  loaded: false,
  loading: false,
  permissionsLoaded: false,
  subscriptionLoaded: false,

  load: async () => {
    const version = ++loadVersion;
    set({ loading: true });
    try {
      const [permissions, subscription] = await Promise.allSettled([
        getPermissions(),
        getSubscriptionPermissions(),
      ]);
      if (version !== loadVersion) return;

      const permissionsLoaded = permissions.status === "fulfilled";
      const subscriptionLoaded = subscription.status === "fulfilled";
      const permissionsValue = permissionsLoaded ? permissions.value : [];

      const subscriptionRecord =
        subscription.status === "fulfilled" ? subscription.value : null;

      const addonPermissions = subscriptionRecord?.addonPermissions ?? [];

      set({
        permissions: permissionsValue,
        subscriptionPermissions: buildSubscriptionPermissions(
          subscriptionRecord?.permissions,
          addonPermissions,
        ),
        addonPermissions,
        planPermissions: subscriptionRecord?.permissions ?? [],
        subscriptionStatus: subscriptionRecord?.status,
        planCode: subscriptionRecord?.planCode,
        cardStatus: subscriptionRecord?.cardStatus,
        currentTermEndsAt: subscriptionRecord?.currentTermEndsAt,
        trialEndsAt: subscriptionRecord?.trialEndsAt,
        loaded: true,
        loading: false,
        permissionsLoaded,
        subscriptionLoaded,
      });
    } catch {
      if (version !== loadVersion) return;
      set({
        loaded: true,
        loading: false,
        permissionsLoaded: false,
        subscriptionLoaded: false,
      });
    }
  },

  reset: () => {
    ++loadVersion;
    set({
      permissions: [],
      subscriptionPermissions: [],
      planPermissions: [],
      addonPermissions: [],
      subscriptionStatus: undefined,
      planCode: undefined,
      cardStatus: undefined,
      currentTermEndsAt: undefined,
      trialEndsAt: undefined,
      loaded: false,
      loading: false,
      permissionsLoaded: false,
      subscriptionLoaded: false,
    });
  },
}));
